import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, Star, ShoppingBag, Ruler, Bell, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { API, inr, fmtErr } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import ProductCard from "../components/ProductCard";

export default function ProductDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [related, setRelated] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [img, setImg] = useState(0);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [qty, setQty] = useState(1);
  const [tab, setTab] = useState("description");
  const [showGuide, setShowGuide] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [added, setAdded] = useState(false);
  const { addItem } = useCart();
  const { user, toggleWishlist } = useAuth();

  useEffect(() => {
    setP(null); setSize(""); setColor(""); setQty(1); setImg(0);
    API.get(`/products/${slug}`).then((r) => {
      setP(r.data);
      document.title = `${r.data.name} | Dressedin by Urbanz`;
      if (r.data.colors.length) setColor(r.data.colors[0]);
      API.get(`/products/${r.data.id}/related`).then((x) => setRelated(x.data)).catch(() => {});
      API.get(`/products/${r.data.id}/reviews`).then((x) => setReviews(x.data)).catch(() => {});
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const variant = useMemo(() => p?.variants.find((v) => v.size === size && v.color === color), [p, size, color]);
  const stockForSize = (s) => p?.variants.filter((v) => v.size === s && v.color === color).reduce((a, v) => a + v.stock, 0) ?? 0;

  if (!p) return <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground">Loading...</div>;

  const price = p.sale_price || p.price;
  const off = p.sale_price ? Math.round((1 - p.sale_price / p.price) * 100) : 0;
  const wished = user && (user.wishlist || []).includes(p.id);
  const inStock = variant && variant.stock > 0;

  const add = () => {
    if (!size) return toast.error("Select a size first");
    if (!inStock) return toast.error("This variant is out of stock");
    addItem(p, size, color, qty);
    setAdded(true);
    toast.success("Added to cart");
    setTimeout(() => setAdded(false), 500);
  };

  const notify = async () => {
    try {
      await API.post("/notify-me", { product_id: p.id, size, color, email: notifyEmail || user?.email || "" });
      toast.success("We'll email you when it's back.");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    try {
      await API.post(`/products/${p.id}/reviews`, { rating, comment });
      const x = await API.get(`/products/${p.id}/reviews`);
      setReviews(x.data);
      setComment("");
      toast.success("Review posted");
    } catch (err) { toast.error(fmtErr(err, "Login to post a review")); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="product-detail-page">
      <div className="grid md:grid-cols-2 gap-10 lg:gap-16">
        <div>
          <div className="relative overflow-hidden border border-[#262626] bg-ink-surface aspect-[3/4] group">
            <AnimatePresence mode="wait">
              <motion.img key={img} src={p.images[img]} alt={p.name} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                exit={{ opacity: 0 }} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110 cursor-zoom-in" />
            </AnimatePresence>
            {off > 0 && <span className="absolute top-4 left-4 bg-gold text-ink text-xs font-bold px-3 py-1 uppercase">{off}% off</span>}
          </div>
          {p.images.length > 1 && (
            <div className="flex gap-3 mt-4">
              {p.images.map((im, i) => (
                <button key={im} onClick={() => setImg(i)} data-testid={`thumb-${i}`}
                  className={`h-20 w-16 border transition-colors ${img === i ? "border-gold" : "border-[#262626]"}`}>
                  <img src={im} alt="" className="h-full w-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold font-bold">{p.subcategory}</p>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight mt-2">{p.name}</h1>
          <div className="flex items-center gap-3 mt-3">
            {p.rating > 0 && (
              <span className="flex items-center gap-1 text-sm"><Star size={14} fill="#D4AF37" className="text-gold" /> {p.rating}
                <span className="text-muted-foreground">({p.num_reviews} reviews)</span></span>
            )}
            <span className="text-xs text-muted-foreground">SKU: {variant?.sku || "—"}</span>
          </div>
          <div className="flex items-baseline gap-3 mt-5">
            <span className="font-display font-black text-3xl gold-text" data-testid="product-price">{inr(price)}</span>
            {p.sale_price && <span className="text-muted-foreground line-through">{inr(p.price)}</span>}
          </div>
          <p className="text-xs text-muted-foreground mt-1">Inclusive of all taxes</p>

          <div className="mt-8">
            <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-3">Color — <span className="text-foreground">{color}</span></p>
            <div className="flex gap-2">
              {p.colors.map((c) => (
                <button key={c} onClick={() => setColor(c)} data-testid={`color-${c.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                  className={`px-4 py-2 text-xs border transition-colors ${color === c ? "border-gold text-gold font-bold" : "border-[#262626] text-muted-foreground hover:border-gold/60"}`}>
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground">Size</p>
              <button onClick={() => setShowGuide(!showGuide)} className="flex items-center gap-1 text-xs text-gold hover:underline" data-testid="size-guide-btn">
                <Ruler size={13} /> Size guide
              </button>
            </div>
            {showGuide && (
              <div className="border border-[#262626] bg-ink-surface p-4 mb-4 text-xs" data-testid="size-guide">
                <table className="w-full text-left">
                  <thead><tr className="text-gold"><th className="py-1">Size</th><th>Chest (in)</th><th>Waist (in)</th></tr></thead>
                  <tbody className="text-muted-foreground">
                    {[["S", 36, 28], ["M", 38, 30], ["L", 40, 32], ["XL", 42, 34], ["XXL", 44, 36]].map((r) => (
                      <tr key={r[0]} className="border-t border-[#262626]"><td className="py-1.5">{r[0]}</td><td>{r[1]}</td><td>{r[2]}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="flex flex-wrap gap-2" data-testid="size-selector">
              {p.sizes.map((s) => {
                const st = stockForSize(s);
                return (
                  <button key={s} onClick={() => st > 0 && setSize(s)} disabled={st === 0} data-testid={`size-${s}`}
                    className={`h-11 w-14 text-sm border transition-colors ${size === s ? "border-gold bg-gold text-ink font-bold" : st === 0 ? "border-[#1a1a1a] text-muted-foreground/40 line-through cursor-not-allowed" : "border-[#262626] hover:border-gold/60"}`}>
                    {s}
                  </button>
                );
              })}
            </div>
            {size && variant && (
              <p className={`text-xs mt-2 ${variant.stock <= 4 ? "text-red-400" : "text-muted-foreground"}`} data-testid="stock-status">
                {variant.stock === 0 ? "Out of stock" : variant.stock <= 4 ? `Only ${variant.stock} left — almost gone` : "In stock"}
              </p>
            )}
          </div>

          {size && !inStock ? (
            <div className="mt-8 border border-[#262626] bg-ink-surface p-5" data-testid="notify-me-box">
              <p className="text-sm font-bold flex items-center gap-2"><Bell size={15} className="text-gold" /> Out of stock in {size}/{color}</p>
              <div className="flex gap-2 mt-3">
                <input data-testid="notify-email-input" type="email" value={notifyEmail} onChange={(e) => setNotifyEmail(e.target.value)}
                  placeholder={user?.email || "your@email.com"}
                  className="flex-1 bg-ink border border-[#262626] px-3 py-2.5 text-sm outline-none focus:border-gold" />
                <button onClick={notify} data-testid="notify-me-btn" className="btn-gold px-5 text-xs font-bold uppercase tracking-wider">Notify me</button>
              </div>
            </div>
          ) : (
            <div className="mt-8 flex gap-3">
              <div className="flex items-center border border-[#262626]">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 text-muted-foreground hover:text-gold" data-testid="qty-minus"><Minus size={14} /></button>
                <span className="w-8 text-center text-sm font-bold" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty(qty + 1)} className="p-3 text-muted-foreground hover:text-gold" data-testid="qty-plus"><Plus size={14} /></button>
              </div>
              <button onClick={add} data-testid="add-to-cart-btn"
                className={`btn-gold flex-1 py-4 font-display font-bold text-sm uppercase tracking-[0.2em] flex items-center justify-center gap-2 ${added ? "animate-cart-pop" : ""}`}>
                <ShoppingBag size={16} /> Add to cart
              </button>
              <button onClick={async () => {
                  if (!user) return toast.error("Login to save items");
                  const added = await toggleWishlist(p.id);
                  toast.success(added ? "Added to wishlist" : "Removed from wishlist");
                }} data-testid="detail-wishlist-btn"
                className={`p-4 border transition-colors ${wished ? "border-gold text-gold" : "border-[#262626] text-muted-foreground hover:border-gold"}`}>
                <Heart size={18} fill={wished ? "#D4AF37" : "none"} />
              </button>
            </div>
          )}

          <div className="mt-10 border-t border-[#262626]">
            <div className="flex gap-6 mt-5">
              {["description", `reviews (${reviews.length})`].map((t) => {
                const key = t.startsWith("reviews") ? "reviews" : t;
                return (
                  <button key={key} onClick={() => setTab(key)} data-testid={`tab-${key}`}
                    className={`text-xs uppercase tracking-[0.2em] pb-2 border-b-2 transition-colors ${tab === key ? "border-gold text-gold font-bold" : "border-transparent text-muted-foreground"}`}>
                    {t}
                  </button>
                );
              })}
            </div>
            {tab === "description" ? (
              <p className="text-sm text-foreground/75 leading-relaxed mt-5" data-testid="product-description">{p.description}</p>
            ) : (
              <div className="mt-5 space-y-5" data-testid="reviews-list">
                {reviews.map((r) => (
                  <div key={r.id} className="border border-[#262626] bg-ink-surface p-4">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm">{r.user_name}</span>
                      <span className="flex gap-0.5">{Array(5).fill(0).map((_, i) => (
                        <Star key={`star-${i}`} size={11} fill={i < r.rating ? "#D4AF37" : "none"} className="text-gold" />))}</span>
                    </div>
                    <p className="text-sm text-foreground/75 mt-2">{r.comment}</p>
                  </div>
                ))}
                {reviews.length === 0 && <p className="text-sm text-muted-foreground">No reviews yet. Be the first.</p>}
                {user ? (
                  <form onSubmit={submitReview} className="border border-[#262626] p-4 space-y-3">
                    <div className="flex gap-1">
                      {Array(5).fill(0).map((_, i) => (
                        <button type="button" key={`rate-${i + 1}`} onClick={() => setRating(i + 1)} data-testid={`rate-${i + 1}`}>
                          <Star size={18} fill={i < rating ? "#D4AF37" : "none"} className="text-gold" />
                        </button>
                      ))}
                    </div>
                    <textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} required
                      placeholder="How's the fit, the fabric, the vibe?" rows={3}
                      className="w-full bg-ink border border-[#262626] px-3 py-2 text-sm outline-none focus:border-gold" />
                    <button data-testid="submit-review-btn" className="btn-gold px-6 py-2.5 text-xs font-bold uppercase tracking-wider">Post review</button>
                  </form>
                ) : (
                  <Link to="/auth" className="text-gold text-sm hover:underline">Login to write a review</Link>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-24" data-testid="related-products">
          <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight mb-8">Complete the fit</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-8">
            {related.map((r, i) => <ProductCard key={r.id} product={r} index={i} />)}
          </div>
        </section>
      )}
    </div>
  );
}
